/**
 * The track pit: the first part of an arena that is not a surface.
 *
 * Everything hard in the game so far is something you *arrive at* — a wall, a
 * pillar, a train — and the rule is the same for all of them: you stop, it
 * costs you, and you come off it back into the arena. A hole is the other
 * thing. There is nothing to bounce off. You go in.
 *
 * So the pit is a **region**, tested after a push has moved somebody and before
 * anything they might have hit, because the floor stops before the wall does
 * and a fighter carried past the platform edge is falling — whatever was on the
 * far side of it.
 *
 * What it costs depends on one question, and the question is not about the pit:
 *
 *   empty    a bad landing. A normal environmental hit, and they climb back up.
 *   a train  the only thing in the game that does not care how healthy you are.
 *
 * Pure, and it holds no train. `fight.js` is handed a predicate and asks it —
 * which keeps the pit ignorant of timetables and keeps the train ignorant of
 * the rules, and means an arena could make its pit lethal for any reason it
 * liked without either of them changing.
 */

export class PitConfigError extends Error {}

export const PIT = Object.freeze({
  /** Wellness taken by the fall alone, with nothing down there. */
  damage: 12,
  /** And by the thing that is down there when there is one. */
  lethalDamage: 46,
  /** Frames of reeling after climbing back out. */
  stun: 84,
  /** How far back onto the platform they end up, past its edge. */
  clearance: 0.45,
});

export function validatePit(pit) {
  if (!pit) throw new PitConfigError('there is no pit');
  for (const field of ['minX', 'maxX', 'minZ', 'maxZ']) {
    if (!Number.isFinite(pit[field])) {
      throw new PitConfigError(`pit has a bad ${field}`);
    }
  }
  if (pit.maxX <= pit.minX || pit.maxZ <= pit.minZ) {
    throw new PitConfigError('a pit needs positive width and depth');
  }
  if (!(pitDamage(pit) > 0)) {
    throw new PitConfigError('a pit must cost something to fall into');
  }
  return pit;
}

export const pitDamage = (pit) => pit?.damage ?? PIT.damage;
export const pitLethalDamage = (pit) => pit?.lethalDamage ?? PIT.lethalDamage;

/**
 * Is this point over the hole?
 *
 * The fighter's *centre*, not their body: a fighter whose toes are over the
 * edge has not fallen off it, and the rest of the game already treats where
 * somebody is as a point on the floor.
 */
export function inPit(point, pit) {
  if (!pit || !point) return false;
  return (
    point.x >= pit.minX && point.x <= pit.maxX && point.z >= pit.minZ && point.z <= pit.maxZ
  );
}

/**
 * Where a fighter who survived the fall ends up.
 *
 * Back on the platform, at the nearest point of it to where they went in, with
 * a little clearance so they are not left standing on the lip about to fall
 * straight back in on the next frame — which is the pit's version of the
 * resting-exactly-on-the-threshold problem `separate` has.
 *
 * `bounds` is the arena's walkable floor when it has one; without it the edge
 * of the pit is all there is to go on.
 */
export function climbOut(point, pit, bounds = null) {
  const clearance = pit?.clearance ?? PIT.clearance;

  if (bounds?.shape === 'rect') {
    // The walkable floor already stops short of the drop; land on the nearest
    // point of it rather than in the strip between it and the edge, which
    // nothing else can reach. Nearest, rather than "back towards -z", because
    // the subway's track is the only drop that happens to lie on +z: a roof's
    // parapet is on the far side of the floor.
    return {
      x: clamp(point.x, bounds.minX, bounds.maxX),
      z: clamp(point.z, bounds.minZ, bounds.maxZ),
    };
  }

  // No floor to go on: out through the nearest edge of the hole, and clear of it.
  const exits = [
    { gap: point.x - pit.minX, x: pit.minX - clearance, z: point.z },
    { gap: pit.maxX - point.x, x: pit.maxX + clearance, z: point.z },
    { gap: point.z - pit.minZ, x: point.x, z: pit.minZ - clearance },
    { gap: pit.maxZ - point.z, x: point.x, z: pit.maxZ + clearance },
  ];
  // Ties go to the near side of the subway's track, which is the case every
  // caller without a floor has ever been.
  const best = exits.reduce((a, b) => (b.gap < a.gap ? b : a), exits[2]);
  return { x: best.x, z: best.z };
}

/**
 * The blow a fall into this pit lands, as the same plain hit shape every other
 * kind of damage in the game is described with.
 *
 * `lethal` is the one thing here that is not about the pit, and it is also the
 * only flag in the game that bypasses the survival floor. Every other finisher
 * asks whether the fighter was already in danger first, which is right for a
 * punch and for a wall — you finish somebody who was going down anyway. A train
 * is not a technique and does not need the setup.
 */
export function pitHit(pit, { lethal = false, id = 'pit' } = {}) {
  return {
    strike: pit?.id ?? id,
    kind: 'environmental',
    environmental: true,
    // Read by `isFinisher`, which already finishes anybody in danger on an
    // environmental hit. Stated anyway, because the pit is the one place the
    // flag is the whole point rather than a consequence.
    finisher: true,
    lethal,
    strength: lethal ? 'strong' : 'normal',
    damage: lethal ? pitLethalDamage(pit) : pitDamage(pit),
    consciousnessMultiplier: lethal ? 4 : 2.2,
    // Nothing to rebound off. Falling is not a collision.
    knockback: 0,
    part: 'pit',
    surface: pit?.id ?? id,
    pit: true,
    train: lethal,
  };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
